import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useLiveQuery } from 'dexie-react-hooks'
import { banco } from '../../db/banco'
import { buscarVivo } from '../../db/registroVivo'
import { novoRegistroBase, agoraISO } from '../../db/tipos'
import type { CondicaoPagamento, RegraComissao, TabelaPreco } from '../../db/tipos'
import { apagarLocalEEnfileirar, gravarEmLote } from '../../sync/outbox'
import {
  condicoesEmRascunho, regrasEmRascunho, errosDaPolitica, gravacoesDaPolitica, condicaoPendente,
  CONDICAO_EM_EDICAO_VAZIA, MSG_CONDICAO_PENDENTE,
  type CondicaoRascunho, type RegraRascunho, type ErrosDaPolitica, type GravacaoEmLote, type CondicaoEmEdicao,
} from '../../representadas/rascunhoDaPolitica'
import { MSG_FORMA_PENDENTE } from '../../representadas/formasDePagamento'
import {
  formularioDe, registroDe, abaMudou, formularioMudou, erroDaAba, abaDaUrl,
  type AbaRepresentada, type FormularioRepresentada,
} from '../../representadas/formularioRepresentada'
import { useSalvamento } from '../../componentes/useSalvamento'
import BotaoSalvar from '../../componentes/BotaoSalvar'
import { confirmar } from '../../componentes/confirmar'
import { registrarGuardaDeSaida } from '../../componentes/guardaDeSaida'
import { IconeLapis, IconePredio } from '../produtos/IconesDaFicha'
import AbaIdentificacao from './AbaIdentificacao'
import AbaLocalizacao from './AbaLocalizacao'
import { CampoPedidoMinimo, CamposCreditoEFrete } from './CamposPoliticaComercial'
import CondicoesPagamento from './CondicoesPagamento'
import FormasPagamento from './FormasPagamento'
import TabelasEComissao from './TabelasEComissao'
import { ESTILO_CARTAO_NOVO } from './estilosDaFornecedora'
import QuadroDaImagem from '../../componentes/QuadroDaImagem'
import { gravarLogoDaRepresentada } from '../../representadas/logo'

// Revisao de 24/09 da spec: UM Salvar por aba, e o lapis edita SO a aba
// aberta. Trocar de aba ou sair com alteracao pendente pergunta antes.
const DESCARTAR = 'Descartar as alterações não salvas?'
const ABAS: ReadonlyArray<readonly [AbaRepresentada, string]> = [
  ['identificacao', 'Identificação'],
  ['localizacao', 'Localização'],
  ['comercial', 'Política Comercial'],
]
const VAZIO = formularioDe(undefined)

// Leva 2 (29/09): a aba so edita a comissao PADRAO da fornecedora; a regra
// de cada tabela mora na tela Editar. Fora do rascunho, uma regra de tabela
// com defeito antigo nao trava o Salvar da aba com um vermelho invisivel.
const soPadrao = (regras: RegraComissao[]) => regras.filter((r) => r.tabela_preco_id === null)

export default function FormRepresentada() {
  const { id } = useParams()
  const navegar = useNavigate()
  const [searchParams] = useSearchParams()
  const { estado: estadoSalvamento, executar } = useSalvamento()
  const ehNova = id === undefined
  const existente = useLiveQuery(() => buscarVivo(banco.representadas, id), [id])
  const condicoesSalvas = useLiveQuery(
    () => (id ? banco.condicoes_pagamento.where('representada_id').equals(id).filter((c) => c.apagado_em === null).sortBy('ordem')
      : Promise.resolve<CondicaoPagamento[]>([])),
    [id],
  )
  const regrasSalvas = useLiveQuery(
    () => (id ? banco.regras_comissao.where('representada_id').equals(id).filter((r) => r.apagado_em === null).toArray()
      : Promise.resolve<RegraComissao[]>([])),
    [id],
  )
  // So para validar no Salvar com o MESMO criterio de visibilidade que
  // TabelasEComissao usa para esconder regra de tabela apagada
  // (regraEscondida em rascunhoDaPolitica.ts) -- ver I2 do review do Task 12.
  const tabelasVivas = useLiveQuery(
    () => (id ? banco.tabelas_preco.where('representada_id').equals(id).filter((t) => t.apagado_em === null).toArray()
      : Promise.resolve<TabelaPreco[]>([])),
    [id],
  )
  const tabelasVivasIds = tabelasVivas === undefined ? undefined : new Set(tabelasVivas.map((t) => t.id))

  // Aba inicial vem da URL (Task 16, 25/09): voltar de uma tabela de preco,
  // dos percentuais ou pela seta do cabecalho reabre em Politica Comercial.
  // Lazy initializer: so le o `?aba=` UMA vez, no primeiro render -- trocar
  // de aba na tela nao mexe na URL (requisito 4), entao ignorar mudancas
  // depois do mount e o comportamento certo.
  const [aba, setAba] = useState<AbaRepresentada>(() => abaDaUrl(searchParams.get('aba')))
  const [editando, setEditando] = useState(ehNova)
  const [form, setForm] = useState<FormularioRepresentada>(VAZIO)
  const [erro, setErro] = useState<string | null>(null)
  const [condicoes, setCondicoes] = useState<CondicaoRascunho[]>([])
  const [regras, setRegras] = useState<RegraRascunho[]>([])
  const [errosPolitica, setErrosPolitica] = useState<ErrosDaPolitica>({})
  // Texto do "adicionar condicao" (Task 16): levantado de CondicoesPagamento
  // para o Salvar poder ver o que esta digitado e ainda nao foi aplicado.
  const [condEdicao, setCondEdicao] = useState<CondicaoEmEdicao>(CONDICAO_EM_EDICAO_VAZIA)
  // Texto do "outra forma" (Task 6): mesma ideia do condEdicao acima -- o
  // Salvar precisa ver o que esta digitado e nao aplicado para avisar em vez
  // de descartar em silencio (mesma escolha "1A" das condicoes).
  const [formaDigitada, setFormaDigitada] = useState('')

  const salvo = formularioDe(existente ?? undefined)
  // Sem edicao em curso, a tela segue o banco (outro aparelho pode ter mudado
  // a fornecedora). Durante a edicao NAO -- seria apagar o que ela digitou.
  const editandoRef = useRef(editando)
  editandoRef.current = editando
  useEffect(() => {
    if (!editandoRef.current) setForm(formularioDe(existente ?? undefined))
  }, [existente, editando])
  // `editando` tambem entra nas dependencias: sem isto, um Save que grava uma
  // condicao/regra NOVA (id: null no rascunho) podia deixar o rascunho parado
  // nesse `id: null` se o Dexie emitisse `condicoesSalvas`/`regrasSalvas`
  // ainda com `editando` true (o efeito via a mudanca mas pulava, por causa da
  // guarda) -- ao sair do modo edicao logo em seguida, nada reexecutava o
  // efeito, e o proximo Salvar recriava o mesmo item de novo (duplicata).
  // Com `editando` na lista, sair da edicao por si so dispara o re-seed a
  // partir do que estiver em `condicoesSalvas`/`regrasSalvas` NA HORA.
  useEffect(() => {
    if (!editandoRef.current && condicoesSalvas) setCondicoes(condicoesEmRascunho(condicoesSalvas))
  }, [condicoesSalvas, editando])
  useEffect(() => {
    if (!editandoRef.current && regrasSalvas) setRegras(regrasEmRascunho(soPadrao(regrasSalvas)))
  }, [regrasSalvas, editando])

  function gravacoesExtras(): GravacaoEmLote[] {
    if (ehNova || aba !== 'comercial' || !id || !condicoesSalvas || !regrasSalvas || !tabelasVivas) return []
    return gravacoesDaPolitica({
      representadaId: id, condicoesSalvas, condicoes, regrasSalvas, regras, novo: novoRegistroBase, agora: agoraISO(),
      tabelasVivasIds,
    })
  }

  // Texto digitado no "adicionar condicao" e nao aplicado tambem conta como
  // pendente (Task 16): trocar de aba ou sair sem tocar em Adicionar tem de
  // perguntar "Descartar", como qualquer outra alteracao nao salva.
  const condicaoNaoAdicionada = !ehNova && aba === 'comercial' && condicaoPendente(condEdicao, condicoes)
  const pendente = editando && (ehNova
    ? formularioMudou(VAZIO, form) || formaDigitada.trim() !== ''
    : abaMudou(aba, salvo, form) || gravacoesExtras().length > 0 || condicaoNaoAdicionada
      || (aba === 'comercial' && formaDigitada.trim() !== ''))
  const pendenteRef = useRef(pendente)
  pendenteRef.current = pendente
  // A seta do cabecalho (Layout) tambem pergunta, pela janela do app.
  useEffect(() => {
    if (!editando) return
    return registrarGuardaDeSaida(async () => !pendenteRef.current || confirmar(DESCARTAR, { botao: 'Descartar', perigo: true }))
  }, [editando])

  function descartar() {
    setForm(salvo)
    setErro(null)
    setCondicoes(condicoesEmRascunho(condicoesSalvas ?? []))
    setRegras(regrasEmRascunho(soPadrao(regrasSalvas ?? [])))
    setErrosPolitica({})
    setCondEdicao(CONDICAO_EM_EDICAO_VAZIA)
    setFormaDigitada('')
    setEditando(false)
  }

  async function podeDescartar(): Promise<boolean> {
    return !pendente || confirmar(DESCARTAR, { botao: 'Descartar', perigo: true })
  }

  async function trocarAba(nova: AbaRepresentada) {
    if (nova === aba) return
    if (editando) {
      if (!(await podeDescartar())) return
      descartar()
    }
    setAba(nova)
  }

  async function aoTocarLapis() {
    if (!editando) { setEditando(true); return }
    if (!(await podeDescartar())) return
    descartar()
  }

  async function aoSalvar() {
    const abas: AbaRepresentada[] = ehNova ? ['identificacao', 'localizacao', 'comercial'] : [aba]
    for (const a of abas) {
      const e = erroDaAba(a, form)
      if (e) { setErro(e); return }
    }
    if ((ehNova || aba === 'comercial') && formaDigitada.trim() !== '') { setErro(MSG_FORMA_PENDENTE); return }
    if (!ehNova && aba === 'comercial') {
      // Texto no "adicionar condicao" esquecido bloqueia o Salvar ANTES de
      // qualquer gravacao (Task 16) -- checagem mais especifica primeiro, no
      // mesmo lugar onde a outra mensagem da Politica Comercial aparece.
      if (condicaoPendente(condEdicao, condicoes)) {
        setErro(MSG_CONDICAO_PENDENTE)
        return
      }
      const erros = errosDaPolitica(condicoes, regras, tabelasVivasIds)
      setErrosPolitica(erros)
      if (Object.keys(erros).length > 0) {
        setErro('Corrija os itens marcados em vermelho — nada foi salvo.')
        return
      }
    }
    setErro(null)
    await executar(async () => {
      const registro = registroDe(existente ?? undefined, form, novoRegistroBase)
      const lote: GravacaoEmLote[] = []
      if (ehNova || formularioMudou(salvo, form)) lote.push({ tabela: 'representadas', registro })
      lote.push(...gravacoesExtras())
      await gravarEmLote(lote)
      setCondEdicao(CONDICAO_EM_EDICAO_VAZIA)
      setFormaDigitada('')
      // F1: mesmo com o remount por `key` em App.tsx, sair do modo edicao
      // aqui tambem e defensivo -- se algum dia a rota deixar de remontar,
      // a tela nova continua abrindo em visualizacao.
      setEditando(false)
      if (ehNova) navegar(`/representadas/${registro.id}`, { replace: true })
    })
  }

  async function aoExcluir() {
    if (!id) return
    if (!(await confirmar('Excluir esta fornecedora?', { botao: 'Excluir', perigo: true }))) return
    await apagarLocalEEnfileirar('representadas', id)
    navegar('/representadas', { replace: true })
  }

  return (
    <section className="p-3 space-y-3 bg-slate-50 min-h-full">
      <div className={`${ESTILO_CARTAO_NOVO} space-y-3`}>
        <div className="flex items-center gap-3">
          <span className="shrink-0 w-11 h-11 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center"><IconePredio tamanho={22} /></span>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-bold text-slate-800 truncate">
              {ehNova ? 'Nova fornecedora' : existente?.nome || 'Fornecedora'}
            </h2>
            {/* Pedido dela (28/09): no topo so a etiqueta "Representada ativa"
                e o CNPJ embaixo do nome. Representada nao tem status no banco:
                toda representada cadastrada esta ativa. */}
            {!ehNova && existente?.cnpj && <p className="text-xs text-slate-500">CNPJ {existente.cnpj}</p>}
            {!ehNova && (
              <span className="mt-1 inline-flex items-center gap-1 rounded-lg bg-teal-50 px-2 py-0.5 text-xs font-medium text-teal-800">
                <IconePredio tamanho={14} /> Representada ativa
              </span>
            )}
          </div>
          {!ehNova && (
            <button type="button" onClick={() => void aoTocarLapis()}
              aria-label={editando ? 'Cancelar edição' : 'Editar esta aba'}
              className="shrink-0 flex items-center gap-1 rounded-lg border border-teal-700 px-3 py-1.5 text-sm text-teal-800">
              {editando ? 'Cancelar' : <><IconeLapis tamanho={16} /> Editar</>}
            </button>
          )}
        </div>
        {/* Spec 2026-10-06: logo da fornecedora, grava na hora (nao passa pelo Salvar das abas). */}
        {!ehNova && existente && (
          <QuadroDaImagem testId="logo-fornecedora" tamanho="pequeno" imagem={existente.logo ?? null}
            rotuloVazio="Escolher logo" aoMudar={(logo) => void gravarLogoDaRepresentada(existente.id, logo)} />
        )}
        {!ehNova && (
          <div className="flex gap-1" role="tablist">
            {ABAS.map(([valor, rotulo]) => (
              <button key={valor} type="button" role="tab" aria-selected={aba === valor}
                onClick={() => void trocarAba(valor)}
                className={`flex-1 text-center py-2 px-1 rounded-lg text-xs font-medium ${
                  aba === valor ? 'bg-teal-700 text-white font-semibold' : 'text-slate-600'}`}>
                {rotulo}
              </button>
            ))}
          </div>
        )}
      </div>

      <form onSubmit={(e) => { e.preventDefault(); void aoSalvar() }} className="space-y-4">
        {/* As duas abas ja desenham os proprios cards (28/09). */}
        {(ehNova || aba === 'identificacao') && (
          <AbaIdentificacao form={form} setForm={setForm} editavel={editando} />
        )}
        {(ehNova || aba === 'localizacao') && (
          <AbaLocalizacao form={form} setForm={setForm} editavel={editando} />
        )}
        {(ehNova || aba === 'comercial') && (
          <>
            {ehNova && <h3 className="font-medium text-slate-800 px-1">Política comercial</h3>}
            <CampoPedidoMinimo form={form} setForm={setForm} editavel={editando} />
            {ehNova ? (
              <FormasPagamento form={form} setForm={setForm} editavel={editando}
                digitada={formaDigitada} setDigitada={setFormaDigitada} alturaFixa={false} />
            ) : (
              // Lado a lado (pedido dela): 2 colunas a partir de 340px; abaixo
              // disso, um embaixo do outro. O limite final e medido no celular dela.
              <div className="grid grid-cols-2 max-[339px]:grid-cols-1 gap-2">
                <CondicoesPagamento itens={condicoes} setItens={setCondicoes} edicao={condEdicao} setEdicao={setCondEdicao}
                  erros={errosPolitica} editavel={editando} />
                <FormasPagamento form={form} setForm={setForm} editavel={editando}
                  digitada={formaDigitada} setDigitada={setFormaDigitada} />
              </div>
            )}
            <CamposCreditoEFrete form={form} setForm={setForm} editavel={editando} />
          </>
        )}
        {!ehNova && aba === 'comercial' && id && (
          <TabelasEComissao representadaId={id} regras={regras} setRegras={setRegras}
            regrasSalvas={regrasSalvas ?? []} erros={errosPolitica} editavel={editando}
            condicoes={condicoes} />
        )}
        {erro && <p className="text-sm text-red-800 bg-red-100 rounded-lg px-3 py-2">{erro}</p>}
        {editando && <BotaoSalvar estado={estadoSalvamento} type="submit">Salvar</BotaoSalvar>}
      </form>

      {!ehNova && (
        <button type="button" onClick={() => void aoExcluir()} className="w-full text-red-600 text-sm underline py-2">
          Excluir fornecedora
        </button>
      )}
    </section>
  )
}
